import { Suspense, lazy, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

import { ThemeProvider } from "@/components/ThemeProvider";
import { PushNotificationInit } from "@/components/PushNotificationInit";
import { InstallBanner } from "@/components/pwa/InstallBanner";

// Layouts
import PatientLayout from "./components/layout/PatientLayout";
import PublicPatientLayout from "./components/layout/PublicPatientLayout";
import ProfessionalLayout from "./components/layout/ProfessionalLayout";
import AdminLayout from "./components/layout/AdminLayout";
import TensLayout from "./components/layout/TensLayout";
import { AuthGatePrompt } from "./components/auth/AuthGatePrompt";

// Public pages
import Index from "./pages/Index";
const Auth = lazy(() => import("./pages/Auth"));
const OAuthCallback = lazy(() => import("./pages/OAuthCallback"));
const Urgencia = lazy(() => import("./pages/Urgencia"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TerminosCondiciones = lazy(() => import("./pages/TerminosCondiciones"));
const PoliticaPrivacidad = lazy(() => import("./pages/PoliticaPrivacidad"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

// Dashboards - lazy loaded
const PatientDashboard = lazy(() => import("./pages/dashboards/PatientDashboard"));
const ProfessionalDashboard = lazy(() => import("./pages/dashboards/ProfessionalDashboard"));
const AdminDashboard = lazy(() => import("./pages/dashboards/AdminDashboard"));

// Patient pages - lazy loaded
const BuscarProfesionales = lazy(() => import("./pages/patient/BuscarProfesionales"));
const MisCitas = lazy(() => import("./pages/patient/MisCitas"));
const HistorialMedico = lazy(() => import("./pages/patient/HistorialMedico"));
const Favoritos = lazy(() => import("./pages/patient/Favoritos"));

const ReferidosUsuario = lazy(() => import("./pages/patient/ReferidosUsuario"));
const FaqUsuario = lazy(() => import("./pages/patient/FaqUsuario"));
const SolicitarServicio = lazy(() => import("./pages/patient/SolicitarServicio"));
const SolicitarAtencion = lazy(() => import("./pages/patient/SolicitarAtencion"));
const ResultadosBusqueda = lazy(() => import("./pages/patient/ResultadosBusqueda"));
const ComunidadUsuario = lazy(() => import("./pages/patient/ComunidadUsuario"));
const ProfesionalesArea = lazy(() => import("./pages/patient/ProfesionalesArea"));
const PerfilPublicoProfesional = lazy(() => import("./pages/patient/PerfilPublicoProfesional"));
const AgendarCita = lazy(() => import("./pages/patient/AgendarCita"));
const MiCuenta = lazy(() => import("./pages/patient/MiCuenta"));
const TriageDigital = lazy(() => import("./pages/patient/TriageDigital"));

// Professional pages - lazy loaded
const PerfilProfesional = lazy(() => import("./pages/professional/PerfilProfesional"));
const Disponibilidad = lazy(() => import("./pages/professional/Disponibilidad"));
const CitasProfesional = lazy(() => import("./pages/professional/CitasProfesional"));
const EstadisticasProfesional = lazy(() => import("./pages/professional/EstadisticasProfesional"));
const FacturacionProfesional = lazy(() => import("./pages/professional/FacturacionProfesional"));
const SolicitudesCercanas = lazy(() => import("./pages/professional/SolicitudesCercanas"));

const ReferidosProfesional = lazy(() => import("./pages/professional/ReferidosProfesional"));
const FaqProfesional = lazy(() => import("./pages/professional/FaqProfesional"));
const ComunidadProfesional = lazy(() => import("./pages/professional/ComunidadProfesional"));
const SuscripcionProfesional = lazy(() => import("./pages/professional/SuscripcionProfesional"));
const RedProfesionales = lazy(() => import("./pages/professional/RedProfesionales"));
const ConsultaTelemedicina = lazy(() => import("./pages/telemedicina/ConsultaTelemedicina"));

// Admin pages - lazy loaded
const GestionUsuarios = lazy(() => import("./pages/admin/GestionUsuarios"));
const VerificacionProfesionales = lazy(() => import("./pages/admin/VerificacionProfesionales"));
const ModeracionSolicitudes = lazy(() => import("./pages/admin/ModeracionSolicitudes"));
const ControlPagos = lazy(() => import("./pages/admin/ControlPagos"));
const GestionComunas = lazy(() => import("./pages/admin/GestionComunas"));
const GestionAreas = lazy(() => import("./pages/admin/GestionAreas"));
const ReportesAdmin = lazy(() => import("./pages/admin/ReportesAdmin"));
const ConfiguracionAdmin = lazy(() => import("./pages/admin/ConfiguracionAdmin"));
const NotificacionesAdmin = lazy(() => import("./pages/admin/NotificacionesAdmin"));
const MonitoreoTiempoReal = lazy(() => import("./pages/admin/MonitoreoTiempoReal"));
const GestionContenido = lazy(() => import("./pages/admin/GestionContenido"));
const GestionPWA = lazy(() => import("./pages/admin/GestionPWA"));
const GestionTens = lazy(() => import("./pages/admin/GestionTens"));

// TENS pages - lazy loaded
const TensDashboard = lazy(() => import("./pages/tens/TensDashboard"));
const TensDocumentos = lazy(() => import("./pages/tens/TensDocumentos"));
const TensFirmar = lazy(() => import("./pages/tens/TensFirmar"));
const TensAsistencia = lazy(() => import("./pages/tens/TensAsistencia"));
const TensPagos = lazy(() => import("./pages/tens/TensPagos"));
const TensPerfil = lazy(() => import("./pages/tens/TensPerfil"));
const TensInformes = lazy(() => import("./pages/tens/TensInformes"));

const queryClient = new QueryClient();

// Hook to prevent infinite loading when role doesn't load
function useAuthReady() {
  const auth = useAuth();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (auth.user && !auth.role && !auth.loading) {
      const timer = setTimeout(() => setTimedOut(true), 4000);
      return () => clearTimeout(timer);
    }
    if (auth.role) setTimedOut(false);
  }, [auth.user, auth.role, auth.loading]);

  const isWaiting = auth.loading || (auth.user && !auth.role && !timedOut);

  return { ...auth, isWaiting };
}

// Loading component
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-primary">Cargando...</div>
    </div>
  );
}

// Public browsable route — no auth needed, public layout
function PublicBrowseRoute({ children }: { children: React.ReactNode }) {
  const { user, role, isWaiting } = useAuthReady();

  if (isWaiting) return <LoadingScreen />;

  if (user && role === 'tens') return <Navigate to="/tens" replace />;

  if (user && role && !['admin', 'profesional_independiente', 'medico', 'enfermera', 'tens'].includes(role)) {
    return <PatientLayout>{children}</PatientLayout>;
  }

  if (user) {
    if (role === 'admin') return <Navigate to="/admin" replace />;
    if (role && ['profesional_independiente', 'medico', 'enfermera'].includes(role)) {
      return <ProfessionalLayout>{children}</ProfessionalLayout>;
    }
  }

  return <PublicPatientLayout>{children}</PublicPatientLayout>;
}

// Patient Route - requires auth
function PatientRoute({ children }: { children: React.ReactNode }) {
  const { user, role, isWaiting } = useAuthReady();

  if (isWaiting) return <LoadingScreen />;
  if (!user) return <PublicPatientLayout><AuthGatePrompt /></PublicPatientLayout>;

  if (role === 'admin') return <Navigate to="/admin" replace />;
  if (role === 'tens') return <Navigate to="/tens" replace />;
  if (role && ['profesional_independiente', 'medico', 'enfermera'].includes(role)) {
    return <Navigate to="/profesional" replace />;
  }

  return <PatientLayout>{children}</PatientLayout>;
}

// Professional Route - Only for professionals
function ProfessionalRoute({ children }: { children: React.ReactNode }) {
  const { user, role, isWaiting } = useAuthReady();

  if (isWaiting) return <LoadingScreen />;
  if (!user) return <Navigate to="/auth" replace />;

  const isProfessional = role && ['profesional_independiente', 'medico', 'enfermera'].includes(role);

  if (role === 'admin') return <Navigate to="/admin" replace />;
  if (!isProfessional) return <Navigate to="/dashboard" replace />;

  return <ProfessionalLayout>{children}</ProfessionalLayout>;
}

// Admin Route - Only for admins
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, role, isWaiting } = useAuthReady();

  if (isWaiting) return <LoadingScreen />;
  if (!user) return <Navigate to="/auth" replace />;
  if (role !== 'admin') return <Navigate to="/dashboard" replace />;

  return <AdminLayout>{children}</AdminLayout>;
}

// TENS Route - Only for TENS employees
function TensRoute({ children }: { children: React.ReactNode }) {
  const { user, role, isWaiting } = useAuthReady();

  if (isWaiting) return <LoadingScreen />;
  if (!user) return <Navigate to="/auth?type=tens" replace />;
  if (role !== 'tens' && role !== 'admin') return <Navigate to="/dashboard" replace />;

  return <TensLayout>{children}</TensLayout>;
}

// Smart redirect based on role
function DashboardRedirect() {
  const { user, role, isWaiting } = useAuthReady();

  if (isWaiting) return <LoadingScreen />;
  if (!user) return <Navigate to="/auth" replace />;

  if (role === 'admin') return <Navigate to="/admin" replace />;
  if (role === 'tens') return <Navigate to="/tens" replace />;
  if (role && ['profesional_independiente', 'medico', 'enfermera'].includes(role)) {
    return <Navigate to="/profesional" replace />;
  }

  return <Navigate to="/usuario" replace />;
}

function CanonicalDomainGuard() {
  useEffect(() => {
    const host = window.location.hostname;
    if (host === "www.urgsalud.com") {
      const target = `https://urgsalud.com${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.location.replace(target);
    }
  }, []);

  return null;
}

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <CanonicalDomainGuard />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Index />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/urgencia" element={<Urgencia />} />
        <Route path="/~oauth/callback" element={<OAuthCallback />} />
        <Route path="/terminos" element={<TerminosCondiciones />} />
        <Route path="/privacidad" element={<PoliticaPrivacidad />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        
        {/* Smart redirect from /dashboard */}
        <Route path="/dashboard" element={<DashboardRedirect />} />
        
        {/* ============ PATIENT ROUTES — PUBLIC (browsable) ============ */}
        <Route path="/usuario" element={<PublicBrowseRoute><PatientDashboard /></PublicBrowseRoute>} />
        <Route path="/buscar-profesionales" element={<PublicBrowseRoute><BuscarProfesionales /></PublicBrowseRoute>} />
        <Route path="/paciente/buscar/:especialidad" element={<PublicBrowseRoute><ResultadosBusqueda /></PublicBrowseRoute>} />
        <Route path="/buscar/:especialidad" element={<PublicBrowseRoute><ResultadosBusqueda /></PublicBrowseRoute>} />
        <Route path="/solicitar" element={<PublicBrowseRoute><SolicitarServicio /></PublicBrowseRoute>} />
        <Route path="/solicitar/:areaId" element={<PublicBrowseRoute><SolicitarServicio /></PublicBrowseRoute>} />
        <Route path="/comunidad" element={<PublicBrowseRoute><ComunidadProfesional /></PublicBrowseRoute>} />
        <Route path="/area/:areaSlug" element={<PublicBrowseRoute><ProfesionalesArea /></PublicBrowseRoute>} />
        <Route path="/profesional-perfil/:profesionalId" element={<PatientRoute><PerfilPublicoProfesional /></PatientRoute>} />
        <Route path="/faq" element={<PublicBrowseRoute><FaqUsuario /></PublicBrowseRoute>} />
        
        {/* ============ PATIENT ROUTES — AUTH REQUIRED ============ */}
        <Route path="/solicitar-servicio" element={<PatientRoute><SolicitarAtencion /></PatientRoute>} />
        <Route path="/triage" element={<PatientRoute><TriageDigital /></PatientRoute>} />
        <Route path="/agendar/:profesionalId" element={<PatientRoute><AgendarCita /></PatientRoute>} />
        <Route path="/mis-citas" element={<PatientRoute><MisCitas /></PatientRoute>} />
        <Route path="/historial-medico" element={<PatientRoute><HistorialMedico /></PatientRoute>} />
        <Route path="/favoritos" element={<PatientRoute><Favoritos /></PatientRoute>} />
        <Route path="/referidos" element={<PatientRoute><ReferidosUsuario /></PatientRoute>} />
        <Route path="/mi-cuenta" element={<PatientRoute><MiCuenta /></PatientRoute>} />
        <Route path="/ayuda" element={<PatientRoute><PatientDashboard /></PatientRoute>} />
        <Route path="/telemedicina/:sesionId" element={<PatientRoute><ConsultaTelemedicina /></PatientRoute>} />
        
        {/* ============ PROFESSIONAL ROUTES ============ */}
        <Route path="/profesional" element={<ProfessionalRoute><ProfessionalDashboard /></ProfessionalRoute>} />
        <Route path="/solicitudes" element={<ProfessionalRoute><SolicitudesCercanas /></ProfessionalRoute>} />
        <Route path="/mis-atenciones" element={<ProfessionalRoute><CitasProfesional /></ProfessionalRoute>} />
        <Route path="/disponibilidad" element={<ProfessionalRoute><Disponibilidad /></ProfessionalRoute>} />
        <Route path="/estadisticas" element={<ProfessionalRoute><EstadisticasProfesional /></ProfessionalRoute>} />
        <Route path="/facturacion" element={<ProfessionalRoute><FacturacionProfesional /></ProfessionalRoute>} />
        <Route path="/perfil" element={<ProfessionalRoute><PerfilProfesional /></ProfessionalRoute>} />
        <Route path="/comunidad-profesional" element={<Navigate to="/comunidad" replace />} />
        <Route path="/red-profesionales" element={<ProfessionalRoute><RedProfesionales /></ProfessionalRoute>} />
        
        <Route path="/referidos-profesional" element={<ProfessionalRoute><ReferidosProfesional /></ProfessionalRoute>} />
        <Route path="/suscripcion" element={<ProfessionalRoute><SuscripcionProfesional /></ProfessionalRoute>} />
        <Route path="/faq-profesional" element={<ProfessionalRoute><FaqProfesional /></ProfessionalRoute>} />
        <Route path="/profesional/telemedicina/:sesionId" element={<ProfessionalRoute><ConsultaTelemedicina /></ProfessionalRoute>} />
        
        {/* ============ ADMIN ROUTES ============ */}
        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/usuarios" element={<AdminRoute><GestionUsuarios /></AdminRoute>} />
        <Route path="/admin/verificacion" element={<AdminRoute><VerificacionProfesionales /></AdminRoute>} />
        <Route path="/admin/solicitudes" element={<AdminRoute><ModeracionSolicitudes /></AdminRoute>} />
        <Route path="/admin/pagos" element={<AdminRoute><ControlPagos /></AdminRoute>} />
        <Route path="/admin/comunas" element={<AdminRoute><GestionComunas /></AdminRoute>} />
        <Route path="/admin/areas" element={<AdminRoute><GestionAreas /></AdminRoute>} />
        <Route path="/admin/reportes" element={<AdminRoute><ReportesAdmin /></AdminRoute>} />
        <Route path="/admin/configuracion" element={<AdminRoute><ConfiguracionAdmin /></AdminRoute>} />
        <Route path="/admin/notificaciones" element={<AdminRoute><NotificacionesAdmin /></AdminRoute>} />
        <Route path="/admin/monitoreo" element={<AdminRoute><MonitoreoTiempoReal /></AdminRoute>} />
        <Route path="/admin/contenido" element={<AdminRoute><GestionContenido /></AdminRoute>} />
        <Route path="/admin/pwa" element={<AdminRoute><GestionPWA /></AdminRoute>} />
        <Route path="/admin/tens" element={<AdminRoute><GestionTens /></AdminRoute>} />
        
        {/* ============ TENS ROUTES ============ */}
        <Route path="/tens" element={<TensRoute><TensDashboard /></TensRoute>} />
        <Route path="/tens/documentos" element={<TensRoute><TensDocumentos /></TensRoute>} />
        <Route path="/tens/firmar" element={<TensRoute><TensFirmar /></TensRoute>} />
        <Route path="/tens/asistencia" element={<TensRoute><TensAsistencia /></TensRoute>} />
        <Route path="/tens/pagos" element={<TensRoute><TensPagos /></TensRoute>} />
        <Route path="/tens/perfil" element={<TensRoute><TensPerfil /></TensRoute>} />
        <Route path="/tens/informes" element={<TensRoute><TensInformes /></TensRoute>} />
        
        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}


// App root
const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <PushNotificationInit />
            <InstallBanner />
            <AppRoutes />

          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
